import { createContext, useContext, useState, type ReactNode } from "react";

const STORAGE_KEY = "vyv_brain_zen_mode";

interface BrainPreferencesContextValue {
  zen: boolean;
  setZen: (value: boolean) => void;
}

const BrainPreferencesContext = createContext<BrainPreferencesContextValue | null>(null);

export function BrainPreferencesProvider({ children }: { children: ReactNode }) {
  const [zen, setZenState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const setZen = (value: boolean) => {
    setZenState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value ? "true" : "false");
    } catch (err) {
      console.error("[BrainPreferences] persist failed:", err);
    }
  };

  return (
    <BrainPreferencesContext.Provider value={{ zen, setZen }}>
      {children}
    </BrainPreferencesContext.Provider>
  );
}

export function useBrainPreferences(): BrainPreferencesContextValue {
  const ctx = useContext(BrainPreferencesContext);
  if (!ctx) {
    // Defensive: if a hook is used outside provider, default to zen=false (timed mode)
    // rather than throw — avoids breaking exercise pages if provider tree is wrong.
    console.warn("[useBrainPreferences] used outside provider — returning zen=false default");
    return { zen: false, setZen: () => {} };
  }
  return ctx;
}
